import React, { useState } from "react";
import { BAND } from "../data/content";
import { SectionHeading } from "./Section";
import { Mail, Phone, MapPin, Check, CalendarIcon } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

export default function Booking({ theme }) {
  const isSynth = theme === "synthwave";
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    eventType: "",
    eventDate: "",
    message: "",
  });

  const onChange = (e) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <section
      id="book"
      data-testid="booking-section"
      className={`section-pad ${isSynth ? "bg-[#0a0a1e]" : "bg-[#1c1916]"}`}
    >
      <div className="container-x">
        {/* ─────────── CURRENT SHOWS ─────────── */}
        <div className="mb-16 md:mb-20">
          <SectionHeading
            theme={theme}
            title={isSynth ? "Current // Shows" : "Current Shows"}
          />

          <div
            className={`p-5 rounded-lg border max-w-2xl ${
              isSynth
                ? "border-fuchsia-500/40 bg-[#0f0f2a]/60"
                : "border-[#c44900]/40 bg-[#1c1916]/60"
            }`}
            data-testid="show-showcase"
          >
            <div
              className={`font-display text-xl md:text-2xl uppercase tracking-wide ${
                isSynth ? "text-white" : "text-[#f4ebd8]"
              }`}
            >
              Showcase
            </div>
            <div
              className={`mt-2 leading-relaxed ${
                isSynth ? "text-gray-300" : "text-[#a89f91]"
              }`}
            >
              <div className="text-lg">
                <span
                  className={`uppercase tracking-[0.2em] text-xs mr-2 ${
                    isSynth ? "text-cyan-300" : "text-[#d4af37]"
                  }`}
                >
                  Date
                </span>
                Tuesday, September 29th, 2026
              </div>
              <div className="mt-1 text-lg">
                <span
                  className={`uppercase tracking-[0.2em] text-xs mr-2 ${
                    isSynth ? "text-cyan-300" : "text-[#d4af37]"
                  }`}
                >
                  Venue
                </span>
                The Royal Theatre · 608 College Street, Toronto
              </div>
              <div className="mt-1 text-lg">
                <span
                  className={`uppercase tracking-[0.2em] text-xs mr-2 ${
                    isSynth ? "text-cyan-300" : "text-[#d4af37]"
                  }`}
                >
                  Doors
                </span>
                6:30 pm
                <span className="mx-2 opacity-40">·</span>
                <span
                  className={`uppercase tracking-[0.2em] text-xs mr-2 ${
                    isSynth ? "text-cyan-300" : "text-[#d4af37]"
                  }`}
                >
                  Show
                </span>
                7:30 pm - 8:30 pm
              </div>
              <div
                className={`mt-2 text-lg font-bold ${
                  isSynth ? "text-fuchsia-300" : "text-[#d4af37]"
                }`}
                style={{
                  textShadow: isSynth
                    ? "0 0 10px rgba(255,0,255,0.4)"
                    : undefined,
                }}
              >
                Admission is FREE
              </div>
            </div>
          </div>
        </div>

        {/* ─── visual divider between the two sections ─── */}
        <div
          aria-hidden
          className={`h-px w-full mb-16 md:mb-20 ${
            isSynth
              ? "bg-gradient-to-r from-transparent via-fuchsia-500/40 to-transparent"
              : "bg-gradient-to-r from-transparent via-[#c44900]/40 to-transparent"
          }`}
        />

        {/* ─────────── BOOK THE BAND ─────────── */}
        <SectionHeading
          theme={theme}
          kicker="Booking Inquiry"
          title={isSynth ? "Book // The // Band" : "Book Out Of Touch"}
          sub={
            <>
              <span
                className="block text-xl md:text-2xl"
                style={{
                  color: "#ffea00",
                  fontWeight: 700,
                  textShadow: "0 0 12px rgba(255,234,0,0.45)",
                }}
              >
                Theatres, Casinos, Corporate and select private events.
              </span>
              <span
                className={`block mt-2 text-xl md:text-2xl font-semibold ${
                  isSynth ? "text-fuchsia-300" : "text-[#d4af37]"
                }`}
              >
                Everyone loves the music of Hall &amp; Oates!
              </span>
            </>
          }
        />

        <div className="grid grid-cols-1 sm:grid-cols-12 gap-10 sm:gap-8 md:gap-12 items-start">
          <div className="sm:col-span-5">
            <div
              data-testid="manager-heading"
              className={`mb-4 font-display text-2xl md:text-3xl uppercase tracking-wider ${
                isSynth ? "neon-gradient-text" : "text-[#f4ebd8]"
              }`}
            >
              Sam Boyd
              <span
                className={`ml-3 text-sm md:text-base tracking-[0.25em] align-middle ${
                  isSynth ? "text-cyan-300" : "text-[#d4af37]"
                }`}
              >
                — Manager
              </span>
            </div>
            <div
              data-testid="manager-company"
              className={`-mt-2 mb-6 text-sm md:text-base uppercase tracking-[0.25em] ${
                isSynth ? "text-fuchsia-300" : "text-[#a89f91]"
              }`}
            >
              {BAND.company}
            </div>

            <ul className="space-y-5">
              <li className="flex items-start gap-4">
                <Mail
                  className={isSynth ? "text-cyan-300 mt-1" : "text-[#c44900] mt-1"}
                />
                <div>
                  <div
                    className={`text-xs uppercase tracking-[0.3em] mb-1 ${
                      isSynth ? "text-fuchsia-400" : "font-stamp text-[#a89f91]"
                    }`}
                  >
                    Email
                  </div>
                  <a
                    href={`mailto:${BAND.email}`}
                    data-testid="contact-email"
                    className={`text-lg ${
                      isSynth ? "text-white" : "text-[#f4ebd8]"
                    }`}
                  >
                    {BAND.email}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <Phone
                  className={isSynth ? "text-cyan-300 mt-1" : "text-[#c44900] mt-1"}
                />
                <div>
                  <div
                    className={`text-xs uppercase tracking-[0.3em] mb-1 ${
                      isSynth ? "text-fuchsia-400" : "font-stamp text-[#a89f91]"
                    }`}
                  >
                    Phone
                  </div>
                  <a
                    href={`tel:${BAND.phone.replace(/[^+0-9]/g, "")}`}
                    data-testid="contact-phone"
                    className={`text-lg ${
                      isSynth ? "text-white" : "text-[#f4ebd8]"
                    }`}
                  >
                    {BAND.phone}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <MapPin
                  className={isSynth ? "text-cyan-300 mt-1" : "text-[#c44900] mt-1"}
                />
                <div>
                  <div
                    className={`text-xs uppercase tracking-[0.3em] mb-1 ${
                      isSynth ? "text-fuchsia-400" : "font-stamp text-[#a89f91]"
                    }`}
                  >
                    Based In
                  </div>
                  <div
                    className={`text-lg ${
                      isSynth ? "text-white" : "text-[#f4ebd8]"
                    }`}
                  >
                    {BAND.city} - will travel
                  </div>
                </div>
              </li>
            </ul>
          </div>

          <div className="sm:col-span-7 w-full sm:mt-16 md:mt-20">
            {submitted ? (
              <div
                data-testid="booking-success"
                className={`p-10 text-center ${
                  isSynth ? "neon-card" : "paper-card"
                }`}
              >
                <Check
                  className={`w-12 h-12 mx-auto mb-5 ${
                    isSynth ? "text-cyan-300" : "text-[#c44900]"
                  }`}
                />
                <h3
                  className={`font-display text-3xl md:text-4xl uppercase mb-3 ${
                    isSynth ? "neon-gradient-text" : "text-[#f4ebd8]"
                  }`}
                >
                  Inquiry Received
                </h3>
                <p
                  className={`text-base ${
                    isSynth ? "text-gray-300" : "text-[#a89f91]"
                  }`}
                >
                  Thanks{form.name ? `, ${form.name.split(" ")[0]}` : ""}.
                  We'll be back in touch within 48 hours with availability and a
                  quote.
                </p>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid="booking-form"
                className={`p-8 md:p-10 ${
                  isSynth ? "neon-card" : "paper-card"
                }`}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-xs uppercase tracking-[0.3em] mb-2 font-semibold"
                      style={{
                        color: "#ffea00",
                        textShadow: "0 0 8px rgba(255,234,0,0.35)",
                      }}
                    >
                      Your Name
                    </label>
                    <input
                      id="name"
                      name="name"
                      required
                      value={form.name}
                      onChange={onChange}
                      data-testid="form-name"
                      placeholder="First & Last"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-xs uppercase tracking-[0.3em] mb-2 font-semibold"
                      style={{
                        color: "#ffea00",
                        textShadow: "0 0 8px rgba(255,234,0,0.35)",
                      }}
                    >
                      Email
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={onChange}
                      data-testid="form-email"
                      placeholder="you@email.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                  <div>
                    <label
                      htmlFor="eventType"
                      className="block text-xs uppercase tracking-[0.3em] mb-2 font-semibold"
                      style={{
                        color: "#ffea00",
                        textShadow: "0 0 8px rgba(255,234,0,0.35)",
                      }}
                    >
                      Event Type
                    </label>
                    <input
                      id="eventType"
                      name="eventType"
                      value={form.eventType}
                      onChange={onChange}
                      data-testid="form-event-type"
                      placeholder="Theatres, Casinos, Corporate Events"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="eventDate"
                      className="block text-xs uppercase tracking-[0.3em] mb-2 font-semibold"
                      style={{
                        color: "#ffea00",
                        textShadow: "0 0 8px rgba(255,234,0,0.35)",
                      }}
                    >
                      Event Date
                    </label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          id="eventDate"
                          data-testid="form-event-date"
                          className="w-full text-left flex items-center justify-between px-4 py-3 rounded-md border transition-colors"
                          style={{
                            borderColor: isSynth
                              ? "rgba(255,0,255,0.35)"
                              : "rgba(196,73,0,0.4)",
                            background: isSynth
                              ? "rgba(15,15,42,0.5)"
                              : "rgba(28,25,22,0.6)",
                            color: form.eventDate
                              ? isSynth
                                ? "#ffffff"
                                : "#f4ebd8"
                              : isSynth
                              ? "rgba(255,255,255,0.5)"
                              : "rgba(244,235,216,0.5)",
                          }}
                        >
                          <span>
                            {form.eventDate
                              ? new Date(form.eventDate).toLocaleDateString(
                                  undefined,
                                  {
                                    year: "numeric",
                                    month: "long",
                                    day: "numeric",
                                  }
                                )
                              : "Pick a date"}
                          </span>
                          <CalendarIcon
                            size={16}
                            className={isSynth ? "text-cyan-300" : "text-[#d4af37]"}
                          />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        align="start"
                        className={`w-auto p-0 border ${
                          isSynth
                            ? "bg-[#0f0f2a] border-fuchsia-500/40 text-white"
                            : "bg-[#1c1916] border-[#c44900]/40 text-[#f4ebd8]"
                        }`}
                      >
                        <Calendar
                          mode="single"
                          selected={
                            form.eventDate ? new Date(form.eventDate) : undefined
                          }
                          onSelect={(d) => {
                            if (!d) return;
                            // store as YYYY-MM-DD so it round-trips cleanly
                            const yyyy = d.getFullYear();
                            const mm = String(d.getMonth() + 1).padStart(2, "0");
                            const dd = String(d.getDate()).padStart(2, "0");
                            setForm((p) => ({
                              ...p,
                              eventDate: `${yyyy}-${mm}-${dd}`,
                            }));
                          }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                <div className="mb-7">
                  <label
                    htmlFor="message"
                    className="block text-xs uppercase tracking-[0.3em] mb-2 font-semibold"
                    style={{
                      color: "#ffea00",
                      textShadow: "0 0 8px rgba(255,234,0,0.35)",
                    }}
                  >
                    Tell Us About It
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    value={form.message}
                    onChange={onChange}
                    data-testid="form-message"
                    placeholder="Venue, headcount, vibe, must-play songs..."
                  />
                </div>

                <button
                  type="submit"
                  data-testid="form-submit"
                  className={isSynth ? "neon-btn neon-btn-cyan" : "stamp-btn"}
                >
                  Send Inquiry →
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
